/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import {RayIntersector, RenderDocument} from "js_renderer";
import IScene from "../../scene_interface";
import IDocument from "../../structural_elements/document_interface";
import IModel from "../../structural_elements/model_interface";
import {vec2, vec3} from "gl-matrix";

export default
class Picker {
    protected _sceneAPI: IScene;
    protected _sceneDocument: IDocument;
    protected _renderDocument: RenderDocument;
    protected _enabled: boolean;
    private _paused: boolean;
    protected _callbacks: any;
    protected _highlighOnMove: boolean;
    private viewIndexInVM: number;

    constructor(aRenderDocument: RenderDocument) {
        this._sceneAPI = aRenderDocument.getSceneAPI();
        this._sceneDocument = this._sceneAPI.getDocument();
        this._renderDocument = aRenderDocument;

        this._enabled = false;
        this._paused = false;

        this._callbacks = {};

        this._highlighOnMove = false;
    }

    public setHighlightOnMove(aValue: boolean) {
        this._highlighOnMove = aValue;
    }

    public pausePicking(aValue: boolean) {
        this._paused = aValue;
    }

    public isPaused(): boolean {
        return this._paused;
    }

    public startPicking(): boolean {
        if (this._enabled) {
            return false;
        }

        let view = this._sceneAPI.viewModeModule.getActiveView();
        this.viewIndexInVM = view.getIndexInViewMode(); // remember the index of view in which we register the mouse observer
        view.registerUserInputObserver(this);

        this._enabled = true;

        return true;
    }

    public stopPicking(): boolean {
        if (!this._enabled) {
            return false;
        }

        let view = this._sceneAPI.viewModeModule.getViewWithIndex(this.viewIndexInVM);
        view.unregisterUserInputObserver(this);

        this._callbacks = {};

        this._enabled = false;

        return true;
    }

    public getSceneModel(): IModel {
        let view = this._sceneAPI.viewModeModule.getActiveView();

        if (!view) {
            return null;
        }

        let physMousePosition = view.getLastMousePosition();
        if (!physMousePosition) {
            return null;
        }

        let pickingInfo = this._renderDocument.renderForValues(view, {
            type: "cdwk_geometry_object_uid",
            readPosition: [physMousePosition[0], physMousePosition[1]]
        });

        // the picking info contains reference to shape and a primitiveIndex i.e. index to face (triangles, points, line segments)
        if (pickingInfo.shapeUID) {
            let models = this._renderDocument.getModelsList(false);
            for (let i = 0; i !== models.length; i++) {
                let model = models[i];
                let geometryObject = model.getGeometryObjectByUID(pickingInfo.shapeUID);
                if (geometryObject) {
                    let renderElement = geometryObject.getElement();
                    if (renderElement) {
                        let group = renderElement.getGroup();
                        let renderModel = group._modelDescription.getRenderModel();
                        let sceneAPIModelUID = renderModel.getSceneAPIModelUID();

                        return this._sceneDocument.getModelByUID(sceneAPIModelUID);
                    }
                }
            }
        }

        return null;
    }

    public pickSceneElement(aTypesArray: string[]) {
        let view = this._sceneAPI.viewModeModule.getActiveView();

        if (!view) {
            return null;
        }

        let mousePosition = view.getLastMousePosition();
        if (!mousePosition) {
            return null;
        }

        let pickingInfo = this._renderDocument.renderForValues(view, {
            type: "cdwk_geometry_object_uid",
            readPosition: [mousePosition[0], mousePosition[1]]
        });

        // the picking info contains reference to shape and a primitiveIndex i.e. index to face (triangles, points, line segments)
        if (pickingInfo && pickingInfo.shapeUID) {
            let models = this._renderDocument.getModelsList(true);
            for (let i = 0; i !== models.length; i++) {
                let model = models[i];
                let geometryObject = model.getGeometryObjectByUID(pickingInfo.shapeUID);
                if (geometryObject) {
                    let renderElement = geometryObject.getElement();

                    let foundType = false;
                    for (let j = 0; j < aTypesArray.length && !foundType; j++) {
                        foundType = aTypesArray[j] === renderElement.getType();
                    }

                    if (foundType || (aTypesArray.length === 0)) {
                        let sceneElement = renderElement.getSceneAPIElementReference();
                        if (!sceneElement) { // if scene api element does not exists for picked model element, then we create a general element to be returned
                            sceneElement = this._sceneDocument.createAPIElement(renderElement.getType());
                            sceneElement.attachToRenderElement(renderElement, model);
                        }

                        return sceneElement;
                    } else {
                        return null;
                    }
                }
            }
        }

        return null;
    }

    public getIntersectionPoint(aPhysMousePosition: vec2): vec3 | null {
        let view = this._sceneAPI.viewModeModule.getActiveView();

        if (!view) {
            return null;
        }

        if (!aPhysMousePosition) {
            return null;
        }

        let pickingPrimitiveInfo = this._renderDocument.renderForValues(view, {
            type: "cdwk_primitive_id",
            readPosition: [aPhysMousePosition[0], aPhysMousePosition[1]]
        });

        let lineA = vec3.clone(view.projectWindowPointTo3DPoint(aPhysMousePosition[0], aPhysMousePosition[1], 0.0));
        let lineB = vec3.clone(view.projectWindowPointTo3DPoint(aPhysMousePosition[0], aPhysMousePosition[1], 1.0));

        // the picking info contains reference to shape and a primitiveIndex i.e. index to face (triangles, points, line segments)
        if (pickingPrimitiveInfo.shapeRef) {
            // in order to determine the closest point, we need the primitive points transformed by the current manipulator transformation, if is is active for
            // current model
            let manipulatorTrafo = null;
            let activeObject = this._sceneAPI.activityModule.getActiveObject();
            if (activeObject && activeObject.getUID() === pickingPrimitiveInfo.shapeRef.model.getSceneAPIModelUID()) {
                manipulatorTrafo = this._sceneAPI.activityModule.getCurrentManipulatorTransformation();
            }

            return RayIntersector.getIntersectionPoint(
                pickingPrimitiveInfo.shapeRef.getGeometry(),
                manipulatorTrafo,
                pickingPrimitiveInfo.shapeRef.getPrimitiveSize(),
                pickingPrimitiveInfo.primitiveIndex,
                lineA,
                lineB);
        }

        return null;
    }

    public setOnPickCallbacks(aCallbacks: any) {
        this._callbacks = aCallbacks;
    }
}
